import React, { useEffect, useLayoutEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Nav, { MMNavItem } from './Nav';
import { IAvailableIconOptions } from '../IconSamples';
import kebabCase from 'lodash/kebabCase';

export interface IMegaMenusCTAsProps {
	heading?: string;
	image?: IImage;
	imagePosition: 'Left' | 'Right';
	subheading?: string;
	description?: string;
	date?: string;
	time?: string;
	primaryLink?: ILink;
	secondaryLink?: ILink;
}

export interface IInListCTAProps {
	cta?: ILink;
	/**
	 * @control iconselect
	 */
	ctaIcon?: IAvailableIconOptions['calianIconSelectValues'];
	ctaDescription?: string;
}

export interface IMegaMenusProps extends IBaseElementProps {
	/**
	 * Select a WordPress menu to supply the nav items for this Mega Menu.
	 * @control NavItem[]
	 */
	navs?: MMNavItem[];
	/**
	 * Display the nav items in how many columns?
	 */
	columns?: 'two' | 'four' | 'five';
	navsIcons?: boolean;
	/**
	 * Toggle this setting will make the first two columns menu list occupy the entire column. (Works only in ITCS BU)
	 */
	tallMenuList?: boolean;
	/**
	 * Please note In list CTA only works with ITCS list menus.
	 */
	inListCTA?: IInListCTAProps;
	ctas?: IMegaMenusCTAsProps[];
	ctasBackground: 'Default' | 'Grey';
	ctasHeadingStyle: 'Default' | 'Medium' | 'Small';
	ctasLinkStyle: 'Default' | 'Button' | 'Inline';
	buttons?: ILink[];
	buttonsBackground: 'Default' | 'Grey';
	buttonsStyle: 'Default' | 'Link';
}

/**
 * Calian Mega Menus
 * @icon format-gallery
 * @block
 */
const MegaMenus: React.FC<IMegaMenusProps> = ({
	className,
	classNames = [],
	navs,
	columns = '',
	navsIcons = false,
	tallMenuList,
	inListCTA,
	ctas,
	ctasBackground = 'Default',
	ctasHeadingStyle = 'Default',
	ctasLinkStyle = 'Default',
	buttons,
	buttonsBackground = 'Default',
	buttonsStyle = 'Default',
}) => {
	const ref = useRef(null);
	const mmRef = useRef(null);

	// Sets All Calian Mega Menu last item position and menu height
	useEffect(() => {
		if (
			!mmRef.current ||
			!mmRef.current.classList.contains('all-calian') ||
			mmRef.current.closest(
				'.calian-header__cta-language-select-mobile__all-calian',
			)
		) {
			return;
		}

		const allCalianMenu = mmRef.current as HTMLDivElement;
		// Adding optional chaining to prevent loading error on backend admin
		const menuToggle = (
			allCalianMenu?.closest('.mega-menu') as HTMLDivElement
		)?.previousElementSibling as HTMLAnchorElement;

		if (!menuToggle) {
			return;
		}

		const items = allCalianMenu.querySelectorAll(
			'.calian-mega-menus__navs > .nav > .nav-item',
		) as NodeListOf<HTMLLIElement>;
		const currentNav = allCalianMenu.querySelector(
			'.calian-mega-menus__navs > .nav',
		) as HTMLUListElement;
		const itemHeights = [];
		const calcDistance = () => {
			if (items.length < 1 || items.length < 5) {
				return;
			}
			// Reset the transform first
			items[4].style.transform = '';
			// Select the third item to be the reference
			const ul = items[3].querySelector('ul') as HTMLUListElement;
			// Get the distance between the fifth item and the bottom of the ul
			const distance = Math.floor(
				items[4].getBoundingClientRect().top -
					ul.getBoundingClientRect().bottom -
					32,
			);

			items[4].style.transform = `translateY(-${distance}px)`;
		};
		const calcHeight = () => {
			if (!currentNav) {
				return;
			}

			// Reset the max height first
			currentNav.style.maxHeight = '';
			items.forEach((item) => {
				itemHeights.push(Math.floor(item.offsetHeight));
			});
			// Pick the largest value among the items and set it as the max height
			currentNav.style.maxHeight = `${Math.max(...itemHeights)}px`;
		};

		menuToggle.addEventListener('shown.bs.dropdown', () => {
			calcDistance();
			calcHeight();
		});
		// Reset the transform when the dropdown is hidden
		menuToggle.addEventListener('hidden.bs.dropdown', () => {
			if (!items[4]) {
				return;
			}

			items[4].style.transform = '';
			currentNav.style.maxHeight = '';
		});
		window.addEventListener('resize', () => {
			calcDistance();
			calcHeight();
		});

		return () => {
			menuToggle.removeEventListener('shown.bs.dropdown', () => {
				calcDistance();
				calcHeight();
			});
			menuToggle.removeEventListener('hidden.bs.dropdown', () => {
				if (!items[4]) {
					return;
				}

				items[4].style.transform = '';
				currentNav.style.maxHeight = '';
			});
			window.removeEventListener('resize', () => {
				calcDistance();
				calcHeight();
			});
		};
	}, []);

	// Dynamically set the max height of the mega menu
	useEffect(() => {
		const calcMegaMenuHeight = () => {
			if (!mmRef.current) {
				return;
			}

			if (window.innerWidth >= 1200) {
				const header = document.querySelector(
					'.calian-header',
				) as HTMLElement;
				mmRef.current.style.maxHeight = `${Math.floor(
					window.innerHeight - header?.offsetHeight,
				)}px`;
			} else {
				mmRef.current.style.maxHeight = '';
			}
		};

		calcMegaMenuHeight();
		window.addEventListener('resize', calcMegaMenuHeight);

		return () => {
			window.removeEventListener('resize', calcMegaMenuHeight);
		};
	}, []);

	// adjust height of the in list cta and the fifth item
	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const assignHeight = () => {
			const inListCTA = ref.current.querySelector(
				'.calian-mega-menus__inlist-cta',
			) as HTMLAnchorElement;
			const nav = ref.current.nextElementSibling as HTMLDivElement;

			if (nav) {
				const navItems = nav.querySelectorAll(
					'.nav > .nav-item',
				) as NodeListOf<HTMLElement>;
				if (navItems.length < 1 || navItems.length < 5) {
					return;
				}

				if (window.innerWidth >= 1200) {
					// assign the height of the in list cta to the second column
					navItems[0].style.height =
						Math.floor(inListCTA.offsetHeight - 32) + 'px';

					const x = ref.current.closest('.calian-mega-menus__navs');

					if (
						x.classList.contains(
							'calian-mega-menus__navs--no-ctas',
						) &&
						!x.classList.contains(
							'calian-mega-menus__navs--two-columns',
						) &&
						!x.classList.contains(
							'calian-mega-menus__navs--four-columns',
						) &&
						!x.classList.contains(
							'calian-mega-menus__navs--five-columns',
						)
					) {
						/* I'm changing the logic a bit here to hopefully make it easier
						to understand.
						From here on now, from design, we're always assuming that
						The nav-item with a <ul> inside is the 3rd menu item on WordPress.
						(which is not much different from the previous assumption) */

						/* if the 3rd nav item has a <ul> inside, then the 6th nav item
						(which exists in its own row) needs to be pushed right to align with
						the other "normal" menu items */
						const gapVal = window.innerWidth >= 1400 ? 40 : 32;
						if (
							navItems[5] &&
							navItems[2] &&
							navItems[2].querySelector('ul') !== null
						) {
							const navLink = navItems[3].querySelector(
								'.nav-link',
							) as HTMLElement;
							const navItemDescription =
								navItems[3].querySelector(
									'.nav-item-description',
								) as HTMLElement;

							const navLinkHeight = navLink?.offsetHeight;
							const navItemDescriptionHeight =
								navItemDescription?.offsetHeight;

							// Calculate the combined height of the nav link and the description
							const combinedHeight =
								navLinkHeight + navItemDescriptionHeight;
							const largerNum = combinedHeight;

							// If we have a menu item with children, in the 3rd position
							// then we need to push all the items to the right to have that
							// menu item alignment with the rest of the items
							for (let i = 5; i < navItems.length; i++) {
								if (navItems[i] && i % 2 !== 0) {
									navItems[i].style.gridColumn = '2';
								}
								navItems[i].style.transform = `translateY(-${
									largerNum + gapVal
								}px)`;
							}
						}
					}
				} else {
					// @ts-ignore
					navItems[0].style.height = '';
					// @ts-ignore
					navItems[4].style.transform = '';
				}
			}
		};
		const navToggles = document.querySelectorAll(
			'.calian-header__main-nav .nav-link.dropdown-toggle',
		) as NodeListOf<HTMLAnchorElement>;

		navToggles.forEach((navToggle) => {
			navToggle.addEventListener('click', () => {
				assignHeight();
			});
		});

		assignHeight();
		window.addEventListener('resize', assignHeight);

		return () => {
			window.removeEventListener('resize', assignHeight);
			navToggles.forEach((navToggle) => {
				navToggle.removeEventListener('click', () => {
					assignHeight();
				});
			});
		};
	}, []);

	return (
		<div className={cn('calian-mega-menus', ...classNames)} ref={mmRef}>
			<div className="calian-mega-menus__container bg-white mx-auto">
				<div
					className={cn(
						'calian-mega-menus__wrapper d-flex flex-column mw-xxl-1240px mx-xxl-auto',
						!ctasBackground ||
							(buttonsBackground !== 'Grey' && 'gap-3 mb-xl-6'),
						ctasBackground &&
							buttonsBackground === 'Grey' &&
							'gap-4',
						buttons?.length > 0 ? 'mb-xl-3' : 'mb-xl-6',
						!navsIcons && 'mt-xl-6',
						navsIcons && 'mt-xl-6',
						inListCTA && 'mt-xl-9',
					)}
				>
					<div className="calian-mega-menus__top d-flex flex-column flex-xl-row justify-content-between">
						{(navs || inListCTA?.cta?.href) && (
							<div
								className={cn(
									'calian-mega-menus__navs',
									ctas &&
										ctas.length > 0 &&
										'calian-mega-menus__navs--ctas',
									navsIcons &&
										'calian-mega-menus__navs--icons',

									(!ctas || ctas.length === 0) &&
										'calian-mega-menus__navs--no-ctas',
									columns &&
										`calian-mega-menus__navs--${columns}-columns`,
									inListCTA?.cta?.href &&
										'calian-mega-menus__has-inlist-cta',
									tallMenuList &&
										'calian-mega-menus__tall-columns',
								)}
							>
								{inListCTA?.cta?.href && (
									<div ref={ref}>
										<Button
											{...inListCTA.cta}
											className="calian-mega-menus__inlist-cta"
										>
											{inListCTA?.ctaIcon && (
												<i
													className={`icon icon-${kebabCase(
														inListCTA?.ctaIcon,
													)}`}
												/>
											)}
											{inListCTA.ctaDescription && (
												<span>
													{inListCTA.ctaDescription}
												</span>
											)}
										</Button>
									</div>
								)}
								{navs?.length > 0 && (
									<Nav
										navItems={navs}
										collapseEnabled={false}
										dropdownEnabled={false}
									/>
								)}
							</div>
						)}
						{ctas && ctas.length > 0 && (
							<div
								className={cn(
									'calian-mega-menus__ctas d-none d-xl-flex flex-column gap-3 z-2',
									ctasBackground &&
										ctasBackground === 'Grey' &&
										'calian-mega-menus__ctas--grey px-3 ps-xl-5 pe-xl-0 gap-6',
								)}
							>
								{ctas.map(
									(
										{
											image,
											imagePosition,
											heading,
											subheading,
											description,
											date,
											time,
											primaryLink,
											secondaryLink,
										},
										index,
									) => (
										<div
											key={index}
											className={cn(
												'calian-mega-menus__cta',
												!ctasBackground ||
													(ctasBackground !==
														'Grey' &&
														'p-4'),
												ctasBackground &&
													ctasBackground === 'Grey' &&
													'calian-mega-menus__cta--grey p-0',
											)}
										>
											<div className="d-flex flex-row">
												<div
													className={cn(
														'd-flex gap-3 gap-md-4 flex-column flex-lg-row w-100',
														!ctasBackground ||
															(ctasBackground !==
																'Grey' &&
																'gap-md-5'),
														ctasBackground ||
															(ctasBackground ===
																'Grey' &&
																'gap-md-4'),
													)}
												>
													<div
														className={cn(
															'calian-mega-menus__cta-image-wrapper',
															imagePosition ===
																'Right' &&
																'calian-mega-menus__cta-image-wrapper--right ms-lg-auto',
														)}
													>
														<Image {...image} />
													</div>
													<div>
														{subheading && (
															<div className="calian-mega-menus__cta-subheading">
																{subheading}
															</div>
														)}
														{heading &&
															React.createElement(
																primaryLink?.href
																	? 'a'
																	: 'div',
																{
																	className:
																		cn(
																			'calian-mega-menus__cta-heading',
																			ctasHeadingStyle &&
																				ctasHeadingStyle ===
																					'Medium' &&
																				'calian-mega-menus__cta-heading--medium',
																			ctasHeadingStyle &&
																				ctasHeadingStyle ===
																					'Small' &&
																				'calian-mega-menus__cta-heading--small',
																			primaryLink?.href &&
																				'text-decoration-none',
																		),
																	children:
																		heading,
																	href: primaryLink?.href,
																	target:
																		primaryLink?.target ===
																			'_self' ||
																		!primaryLink?.target
																			? '_parent'
																			: primaryLink?.target,
																	rel:
																		primaryLink?.target ||
																		primaryLink?.target !==
																			'_self'
																			? 'noopener'
																			: '',
																},
															)}
														{description && (
															<div
																className={cn(
																	'calian-mega-menus__cta-description',
																	date ||
																		(time &&
																			'mb-2'),
																)}
															>
																{description}
																{primaryLink &&
																	ctasLinkStyle ===
																		'Inline' &&
																	!secondaryLink && (
																		<Button
																			className={cn(
																				'calian-mega-menus__cta-link calian-mega-menus__cta-link--inline text-decoration-none',
																			)}
																			{...primaryLink}
																		/>
																	)}
															</div>
														)}
														{date && time && (
															<div className="mt-2">
																{date && (
																	<div className="calian-mega-menus__cta-date d-inline-block">
																		{date}
																	</div>
																)}
																{date &&
																	time && (
																		<div className="calian-mega-menus__cta-break d-inline-block"></div>
																	)}
																{time && (
																	<div className="calian-mega-menus__cta-time d-inline-block">
																		{time}
																	</div>
																)}
															</div>
														)}
														{primaryLink &&
															ctasLinkStyle !==
																'Inline' &&
															!secondaryLink && (
																<div className="mt-2">
																	<Button
																		className={cn(
																			'calian-mega-menus__cta-link text-decoration-none',
																			ctasLinkStyle &&
																				ctasLinkStyle ===
																					'Button' &&
																				'calian-mega-menus__cta-link--button btn btn-sm btn-secondary',
																		)}
																		{...primaryLink}
																	/>
																</div>
															)}
													</div>
												</div>
											</div>
											{ctasBackground === 'Grey' &&
												ctasLinkStyle !== 'Default' &&
												(primaryLink ||
													secondaryLink) && (
													<div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-start gap-3 mt-4">
														{(primaryLink?.href ||
															primaryLink?.label) &&
															primaryLink?.label !==
																'<br>' && (
																<Button
																	className={cn(
																		'calian-mega-menus__cta-link text-decoration-none',
																		ctasLinkStyle &&
																			ctasLinkStyle ===
																				'Button' &&
																			'calian-mega-menus__cta-link--button btn btn-sm btn-secondary',
																	)}
																	{...primaryLink}
																/>
															)}
														{(secondaryLink?.href ||
															secondaryLink?.label) &&
															secondaryLink?.label !==
																'<br>' && (
																<Button
																	className="calian-mega-menus__cta-link calian-mega-menus__cta-link--secondary"
																	{...secondaryLink}
																/>
															)}
													</div>
												)}
										</div>
									),
								)}
							</div>
						)}
					</div>
					{buttons && buttons?.length > 0 && (
						<div
							className={cn(
								'calian-mega-menus__bottom d-none d-xl-flex pt-3 px-3 px-xl-0 position-relative z-2',
								buttonsBackground === 'Grey' &&
									'calian-mega-menus__bottom--grey',
							)}
						>
							<div
								className={cn(
									'calian-mega-menus__buttons d-flex flex-column flex-sm-row',
									buttonsStyle !== 'Link' && 'gap-2',
									buttonsStyle === 'Link' &&
										'calian-mega-menus__button-wrapper--link',
								)}
							>
								{buttons.map((button, index) => (
									<div
										className={cn(
											'calian-mega-menus__button-wrapper',
											buttonsStyle === 'Link' &&
												'calian-mega-menus__button-wrapper--link',
										)}
										key={index}
									>
										<Button
											key={index}
											className={cn(
												buttonsStyle !== 'Link' &&
													'btn btn-sm btn-secondary',
												buttonsStyle === 'Link' &&
													'calian-mega-menus__button',
											)}
											{...button}
										/>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
export default MegaMenus;
